import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import {
  DescriptionCardSliceDefault,
  DescriptionCardSliceWithActionButton,
} from '../../../prismicio-types';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

enum SLICE_VARIATION {
  default = 'default',
  withActionButton = 'withActionButton',
}

/**
 * Props for `DescriptionCard`.
 */
export type DescriptionCardProps =
  SliceComponentProps<Content.DescriptionCardSlice>;

/**
 * Component for "DescriptionCard" Slices.
 */
const DescriptionCard = ({ slice }: DescriptionCardProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-5 md:px-16 py-16 md:py-28"
    >
      {slice.variation === SLICE_VARIATION.default && (
        <DefaultDescriptionCard slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withActionButton && (
        <DescriptionCardWithActionButton slice={slice} />
      )}
    </section>
  );
};

const DefaultDescriptionCard = ({
  slice,
}: {
  slice: DescriptionCardSliceDefault;
}): JSX.Element => {
  const {
    primary: { image, icon, title, description },
  } = slice;

  return (
    <div className="flex flex-col-reverse md:flex-row gap-20 text-white items-center">
      <PrismicNextImage
        field={image}
        className="w-full md:w-1/2 rounded-[20px]"
      />
      <div className="flex flex-col gap-y-6">
        <PrismicNextImage field={icon} />
        <PrismicRichText field={title} />
        <PrismicRichText
          field={description}
          components={{
            paragraph: ({ children }) => (
              <p className="text-medium-normal">{children}</p>
            ),
          }}
        />
      </div>
    </div>
  );
};

const DescriptionCardWithActionButton = ({
  slice,
}: {
  slice: DescriptionCardSliceWithActionButton;
}): JSX.Element => {
  const {
    primary: {
      image,
      title,
      description,
      top_text: topText,
      go_to_slice: goToSlice,
      button_label: buttonLabel,
    },
  } = slice;

  return (
    <div className="flex flex-col-reverse md:flex-row gap-20 text-white items-center">
      <PrismicNextImage
        field={image}
        className="w-full md:w-1/2 rounded-[20px]"
      />
      <div className="flex flex-col gap-y-6">
        <PrismicRichText field={topText} />
        <PrismicRichText field={title} />
        <PrismicRichText
          field={description}
          components={{
            paragraph: ({ children }) => (
              <p className="text-medium-normal">{children}</p>
            ),
          }}
        />
        <div className="mt-8">
          <PrismicNextLink
            href={`#${goToSlice}`}
            className="text-white font-semibold border-2 rounded-lg px-6 py-4 border-green-500 "
          >
            <span>{buttonLabel}</span>
          </PrismicNextLink>
        </div>
      </div>
    </div>
  );
};

export default DescriptionCard;
